import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import { Directive, Renderer2, inject, input, signal, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { PULL_TO_REFRESH_CONFIG, PTR_STATE } from './pull-to-refresh.config';

type PullState = (typeof PTR_STATE)[keyof typeof PTR_STATE];

@Directive({
  selector: '[kPullToRefresh]',
  standalone: true,
})
export class PullToRefreshDirective implements OnInit, OnDestroy {
  disabled = input<boolean>(false);

  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  private initialized = false;
  private pullState = signal<PullState>(PTR_STATE.IDLE);
  private pullDistance = 0;
  private startY = 0;
  private currentY = 0;
  private scrollTopAtStart = 0;
  private pullIndicator: HTMLElement | null = null;
  private touchStartHandler?: EventListener;
  private touchMoveHandler?: EventListener;
  private touchEndHandler?: EventListener;
  private refreshTimeoutId?: ReturnType<typeof setTimeout>;
  private reloadTimeoutId?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeDirective();
      this.initialized = true;
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private getEffectiveScrollTop(touchTarget: EventTarget | null): number {
    if (window.scrollY > 0) return window.scrollY;
    let el = touchTarget as HTMLElement | null;
    while (el && el !== this.document.body) {
      if (el.scrollTop > 0) return el.scrollTop;
      el = el.parentElement;
    }
    return 0;
  }

  private initializeDirective(): void {
    this.renderer.setStyle(this.document.documentElement, 'overscroll-behavior-y', 'none');

    this.touchStartHandler = this.onTouchStart.bind(this);
    this.touchMoveHandler = this.onTouchMove.bind(this);
    this.touchEndHandler = this.onTouchEnd.bind(this);

    this.document.addEventListener('touchstart', this.touchStartHandler, { passive: true });
    this.document.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    this.document.addEventListener('touchend', this.touchEndHandler, { passive: true });
  }

  private onTouchStart(event: Event): void {
    if (this.disabled() || this.pullState() === PTR_STATE.REFRESHING) {
      return;
    }

    const touchEvent = event as TouchEvent;
    this.scrollTopAtStart = this.getEffectiveScrollTop(touchEvent.target);

    if (this.scrollTopAtStart === 0) {
      this.startY = touchEvent.touches[0].clientY;
      this.currentY = this.startY;
    }
  }

  private onTouchMove(event: Event): void {
    if (this.disabled() || this.pullState() === PTR_STATE.REFRESHING || this.scrollTopAtStart !== 0) {
      return;
    }

    const touchEvent = event as TouchEvent;
    this.currentY = touchEvent.touches[0].clientY;
    const deltaY = this.currentY - this.startY;

    if (deltaY > 0) {
      event.preventDefault();

      this.pullDistance = Math.min(
        deltaY / PULL_TO_REFRESH_CONFIG.DRAG_RESISTANCE_FACTOR,
        PULL_TO_REFRESH_CONFIG.MAX_INDICATOR_DISTANCE_PX,
      );

      if (this.pullDistance >= PULL_TO_REFRESH_CONFIG.TRIGGER_THRESHOLD_PX) {
        this.pullState.set(PTR_STATE.READY);
      } else {
        this.pullState.set(PTR_STATE.PULLING);
      }

      this.updateIndicator();
    }
  }

  private onTouchEnd(): void {
    if (this.disabled() || this.pullState() === PTR_STATE.IDLE || this.pullState() === PTR_STATE.REFRESHING) {
      return;
    }

    if (this.pullDistance >= PULL_TO_REFRESH_CONFIG.TRIGGER_THRESHOLD_PX && this.pullState() === PTR_STATE.READY) {
      this.triggerRefresh();
    } else {
      this.resetPull();
    }
  }

  private updateIndicator(): void {
    if (!this.pullIndicator) {
      this.createIndicator();
    }

    if (this.pullIndicator) {
      const ratio = this.pullDistance / PULL_TO_REFRESH_CONFIG.TRIGGER_THRESHOLD_PX;
      const indicatorY = ratio * (PULL_TO_REFRESH_CONFIG.INDICATOR_REFRESHING_Y_PX + PULL_TO_REFRESH_CONFIG.INDICATOR_SIZE_PX) - PULL_TO_REFRESH_CONFIG.INDICATOR_SIZE_PX;
      this.renderer.setStyle(
        this.pullIndicator,
        'transform',
        `translate3d(-50%, ${ indicatorY }px, 0)`,
      );
      this.renderer.setStyle(this.pullIndicator, 'opacity', '1');

      const icon = this.pullIndicator.querySelector('.ptr-icon');
      if (icon) {
        const angle = Math.min(this.pullDistance / PULL_TO_REFRESH_CONFIG.TRIGGER_THRESHOLD_PX, 1) * 180;
        this.renderer.setStyle(icon, 'transform', `rotate(${ angle }deg)`);
      }
    }
  }

  private createIndicator(): void {
    this.pullIndicator = this.renderer.createElement('div');
    this.renderer.addClass(this.pullIndicator, 'ptr-indicator');
    this.renderer.setStyle(this.pullIndicator, 'position', 'fixed');
    this.renderer.setStyle(this.pullIndicator, 'top', '0');
    this.renderer.setStyle(this.pullIndicator, 'left', '50%');
    this.renderer.setStyle(this.pullIndicator, 'width', '48px');
    this.renderer.setStyle(this.pullIndicator, 'height', '48px');
    this.renderer.setStyle(this.pullIndicator, 'border-radius', '50%');
    this.renderer.setStyle(
      this.pullIndicator,
      'transform',
      `translate3d(-50%, -${ PULL_TO_REFRESH_CONFIG.INDICATOR_SIZE_PX }px, 0)`,
    );
    this.renderer.setStyle(this.pullIndicator, 'display', 'flex');
    this.renderer.setStyle(this.pullIndicator, 'align-items', 'center');
    this.renderer.setStyle(this.pullIndicator, 'justify-content', 'center');
    this.renderer.setStyle(this.pullIndicator, 'z-index', '1000');
    this.renderer.setStyle(this.pullIndicator, 'background-color', 'rgba(12, 12, 14, 0.8)');
    this.renderer.setStyle(this.pullIndicator, 'opacity', '0');
    this.renderer.setStyle(this.pullIndicator, 'transition', 'transform 0.3s ease-out, opacity 0.2s ease-out');
    this.renderer.setStyle(this.pullIndicator, 'pointer-events', 'none');

    const icon = this.renderer.createElement('span');
    this.renderer.addClass(icon, 'material-symbols-outlined');
    this.renderer.addClass(icon, 'ptr-icon');
    this.renderer.addClass(icon, 'text-neon');
    this.renderer.setStyle(icon, 'font-size', '32px');
    icon.textContent = 'refresh';

    this.renderer.appendChild(this.pullIndicator, icon);
    this.renderer.appendChild(this.document.body, this.pullIndicator);
  }

  private triggerRefresh(): void {
    this.pullState.set(PTR_STATE.REFRESHING);

    if (this.pullIndicator) {
      this.renderer.setStyle(
        this.pullIndicator,
        'transform',
        `translate3d(-50%, ${ PULL_TO_REFRESH_CONFIG.INDICATOR_REFRESHING_Y_PX }px, 0)`,
      );

      const icon = this.pullIndicator.querySelector('.ptr-icon');
      if (icon) {
        const currentAngle = Math.min(this.pullDistance / PULL_TO_REFRESH_CONFIG.TRIGGER_THRESHOLD_PX, 1) * 180;
        const delaySeconds = -(currentAngle / 360);
        this.renderer.removeStyle(icon, 'transform');
        this.renderer.setStyle(icon, 'animation', `spin 1s linear ${ delaySeconds }s infinite`);
      }
    }

    this.refreshTimeoutId = setTimeout(() => {
      const icon = this.pullIndicator?.querySelector('.ptr-icon');
      if (icon) {
        this.renderer.removeStyle(icon, 'animation');
        this.renderer.setStyle(icon, 'transform', 'rotate(0deg)');
        icon.textContent = 'done';
      }

      this.reloadTimeoutId = setTimeout(() => {
        this.document.location.reload();
      }, PULL_TO_REFRESH_CONFIG.TICK_DISPLAY_MS);
    }, PULL_TO_REFRESH_CONFIG.RELOAD_DELAY_MS);
  }

  private resetPull(): void {
    this.pullState.set(PTR_STATE.IDLE);
    this.pullDistance = 0;
    this.startY = 0;
    this.currentY = 0;

    if (this.pullIndicator) {
      this.renderer.setStyle(
        this.pullIndicator,
        'transform',
        `translate3d(-50%, -${ PULL_TO_REFRESH_CONFIG.INDICATOR_SIZE_PX }px, 0)`,
      );
      this.renderer.setStyle(this.pullIndicator, 'opacity', '0');

      const icon = this.pullIndicator.querySelector('.ptr-icon');
      if (icon) {
        this.renderer.removeStyle(icon, 'animation');
        this.renderer.setStyle(icon, 'transform', 'rotate(0deg)');
        icon.textContent = 'refresh';
      }
    }
  }

  private cleanup(): void {
    if (this.refreshTimeoutId !== undefined) {
      clearTimeout(this.refreshTimeoutId);
    }
    if (this.reloadTimeoutId !== undefined) {
      clearTimeout(this.reloadTimeoutId);
    }
    if (this.initialized) {
      if (this.touchStartHandler) {
        this.document.removeEventListener('touchstart', this.touchStartHandler);
      }
      if (this.touchMoveHandler) {
        this.document.removeEventListener('touchmove', this.touchMoveHandler);
      }
      if (this.touchEndHandler) {
        this.document.removeEventListener('touchend', this.touchEndHandler);
      }

      if (this.pullIndicator && this.pullIndicator.parentNode) {
        this.renderer.removeChild(this.document.body, this.pullIndicator);
      }
    }
  }
}
